import React, { useEffect, useState, useMemo } from 'react'
import BreadCrumbs from '../../../components/BreadCrumbs';
import { Alert, Card, CardBody, Col, Row } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import TableLoadingComponent from '../../../components/TableLoadingComponent';
import ViewButtonComponent from '../../../components/ViewButtonComponent';
import Select from "react-select";
import { REFUND_ORDER_STATUS } from '../../../constants/constans';
import { Badge, Input, Label } from 'reactstrap';
import { ReturnRequestsDetails, useLazyGetReturnRequestsQuery } from '../../../redux/services/returnRequestService';
import { setRowsPerPage } from '../../../redux/returnRequestSlice';

const ReturnRequests = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const restaurantId = useSelector(
        (state: any) => state.restaurant.selectedRestaurantDetails.id
    );

    const rowsPerPage = useSelector((state: any) => state.refundOrder.rowsPerPage);

    const [allRequests, setAllRequests] = useState<ReturnRequestsDetails[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>("All");

    // date filter (YYYY-MM-DD) — initialize to today's date
    const [startDate, setStartDate] = useState<string>(() => {
        return new Date().toISOString().split("T")[0];
    });

    const [triggerReturnRequests, { data, isLoading, error }] = useLazyGetReturnRequestsQuery();

    useEffect(() => {
        triggerReturnRequests({ restaurantId, status: "All" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerReturnRequests, restaurantId]);

    useEffect(() => {
        if (data) {
            setAllRequests(data);
        }
    }, [data]);

    const filteredRequests = useMemo(() => {
        // Start from status filtered list
        let list = selectedStatus === "All" ? allRequests : allRequests.filter(request => request.status === selectedStatus);

        // If a date is selected, filter by createdAt date (convert seconds to YYYY-MM-DD)
        if (startDate && startDate.trim() !== "") {
            list = list.filter((request) => {
                const createdAt = request.createdAt;
                if (!createdAt) return false;
                const reqDateISO = new Date(createdAt._seconds * 1000).toISOString().split("T")[0];
                return reqDateISO === startDate;
            });
        }

        return list;
    }, [allRequests, selectedStatus, startDate]);

    const handleStatusChange = (selectedOption: { value: string; label: string } | null) => {
        const newStatus = selectedOption ? selectedOption.value : "All";
        setSelectedStatus(newStatus);
    };

    const handleViewReturnRequest = (request: ReturnRequestsDetails) => {
        navigate('/payments/return-requests/view-return-request', { state: { request } });
    };

    const handleChangeRowsPerPage = (newRowsPerPage: number) => {
        dispatch(setRowsPerPage(newRowsPerPage));
    };

    const handleOrderDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(e.target.value); // value will be "" or "YYYY-MM-DD"
    };

    const getStatusBadgeColor = (status: string) => {
        switch (status) {
            case "Pending":
                return "warning";
            case "Rejected":
                return "danger";
            case "Completed":
                return "success";
            default:
                return "secondary";
        }
    };

    const getStatusDisplayText = (status: string) => {
        switch (status) {
            case "Pending":
                return "Pending";
            case "Rejected":
                return "Rejected";
            case "Completed":
                return "Completed";
            default:
                return "Cancelled";
        }
    };

    const columns: TableColumn<ReturnRequestsDetails>[] = [
        {
            name: <h6 className="fw-bold">Request Id</h6>,
            selector: (row) => row.id,
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">Cart Name</h6>,
            selector: (row) => row.cart.displayName || '',
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">Status</h6>,
            cell: (row) => (
                <div>
                    <Badge
                        color={getStatusBadgeColor(row?.status)}
                        className="px-4 py-2"
                    >
                        <div style={{ fontSize: 12 }}>
                            {getStatusDisplayText(row?.status)}
                        </div>
                    </Badge>
                </div>
            ),
            sortable: true,
        },
        {
            name: <h6 className="fw-bold">Action</h6>,
            cell: (row) => (
                <div className="d-flex gap-2">
                    <ViewButtonComponent onClick={() => handleViewReturnRequest(row)} />
                </div>
            ),
            right: true,
            ignoreRowClick: true,
        },
    ];

    return (
        <>
            <BreadCrumbs hasSubMenus={true} />
            <Row className='mt-3 mb-3 d-flex justify-content-between'>
                <Col lg="4">
                    <h4>Cart Return Requests</h4>
                </Col>
                <Col lg="4" className="mb-3">
                    <Label>Request Date</Label>
                    <Input
                        type="date"
                        value={startDate || ""}
                        onChange={handleOrderDateChange}
                    />
                </Col>
                <Col lg="4" className="mb-3 d-flex flex-column align-items-start">
                    <Label>Request Status</Label>
                    <Select
                        options={REFUND_ORDER_STATUS}
                        onChange={handleStatusChange}
                        value={{
                            value: selectedStatus,
                            label: REFUND_ORDER_STATUS.find(option => option.value === selectedStatus)?.label || "All"
                        }}
                        className='w-100'
                        placeholder="Filter by status..."
                        isSearchable={false}
                    />
                </Col>
            </Row>
            <Card>
                <CardBody className="p-3">
                    {isLoading ? (
                        <TableLoadingComponent numColumns={4} />
                    ) : error ? (
                        <Alert variant="danger">
                            Failed to load return requests. Please try again.
                        </Alert>
                    ) : (
                        <>
                            <DataTable
                                columns={columns}
                                data={filteredRequests}
                                pagination
                                highlightOnHover
                                noDataComponent={
                                    <div className='p-5 text-center'>
                                        There are no records to display
                                    </div>
                                }
                                responsive
                                onChangeRowsPerPage={handleChangeRowsPerPage}
                                paginationPerPage={rowsPerPage}
                                paginationComponentOptions={{
                                    rowsPerPageText: 'Rows per page:',
                                    rangeSeparatorText: 'of',
                                    noRowsPerPage: false,
                                    selectAllRowsItem: false,
                                }}
                            />
                        </>
                    )}
                </CardBody>
            </Card>
        </>
    );
};

export default ReturnRequests;
